import React from 'react'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import copy from 'copy-to-clipboard'
import classNames from 'classnames'

import {
  PenIcon,
  PenCrossedIcon,
  ExternalLinkIcon,
  InfoIcon,
  ArrowRightIcon,
  RecentIcon,
  LinkIcon,
  NewTemplateIcon,
  FilePDFIcon,
  FileMDIcon,
  DuplicateIcon,
  UndoIcon,
  DeleteIcon,
  FavoriteEmptyIcon,
  FavoriteIcon,
} from 'components/Utils/Icons'
import { Toggle } from 'components/base'
import { selectors as NotesSeletors, actions as NotesActions } from 'data/notes'
import {
  createSuccessToasterMessage,
  enableDetachedMode,
} from 'data/ui/actions'
import { isArchived } from 'data/notes/record'
import { selectors as ChannelsSelectors } from 'data/channels'
import { selectors as OrganizationSelectors } from 'data/organizations'
import {
  currentUserCanManageNote,
  currentUserCanCreateNote,
  currentUserCanEditNote,
  currentUserCanDeleteNote,
} from 'data/rights/selectors'

import { setActiveModal } from 'data/ui/actions'
import { isDetachedModeEnabled } from 'data/ui/selectors'
import {
  NOTES_HISTORY,
  NOTES_INFOS,
  MOVE_TO,
} from 'components/Utils/Modal/constants'
import { getApiToken } from 'data/auth/selectors'
import {
  selectors as UserPreferencesSelectors,
  actions as UserPreferencesActions,
} from 'data/userPreferences'
import { providesUserPreferencesMutations } from 'components/Settings/User/dataSync'
import providesSaveNoteAsTemplate from 'components/Notes/dataSync/providesSaveNoteAsTemplate'
import Menu from 'components/Utils/Menu'
import { isAppDesktop } from 'helpers/DesktopHelpers'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'
import EditProtectionIncentiveModal from './EditProtectionIncentiveModal'
const {
  archiveNotes,
  unarchiveNotes,
  duplicateNote,
  deleteNotes,
  lockNoteForEdition,
  unlockNoteForEdition,
} = NotesActions

const { updateUserPreferences } = UserPreferencesActions

const enhance = compose(
  providesUserPreferencesMutations,
  providesSaveNoteAsTemplate,
  connect(
    (state, { noteId }) => {
      const note = NotesSeletors.getNote(state, noteId)
      const canManage = currentUserCanManageNote(state, note)
      const canDelete = currentUserCanDeleteNote(state, note)
      const canEdit = currentUserCanEditNote(state, note)
      const channel =
        note && ChannelsSelectors.getChannel(state, note.channelId)
      const canCreate = currentUserCanCreateNote(state, channel)
      const canSaveAsTemplate = canEdit
      const isPayingOrganization = OrganizationSelectors.isPaid(state)

      return {
        canDelete,
        canManage,
        canCreate,
        canEdit,
        note,
        channel,
        isArchived: note && isArchived(note),
        isBookmarked:
          note && UserPreferencesSelectors.isNoteBookmarked(state, noteId),
        bookmarkedNoteIds: UserPreferencesSelectors.getBookmarkedNoteIds(state),
        isDetachedModeEnabled: isDetachedModeEnabled(state),
        shareLink: note && note.shareLink,
        apiToken: getApiToken(state),
        canSaveAsTemplate,
        isPayingOrganization,
      }
    },
    {
      setActiveModal,
      archiveNotes,
      unarchiveNotes,
      duplicateNote,
      deleteNotes,
      lockNoteForEdition,
      unlockNoteForEdition,
      createSuccessToasterMessage,
      enableDetachedMode,
      updateUserPreferences,
    }
  )
)

export default enhance(function NoteControlsMenu({
  note,
  isArchived,
  isBookmarked,
  bookmarkedNoteIds,
  updateUserPreferences,
  showListControls = false,
  createSuccessToasterMessage,
  canDelete,
  canManage,
  canCreate,
  canEdit,
  setActiveModal,
  archiveNotes,
  unarchiveNotes,
  duplicateNote,
  deleteNotes,
  isDetachedModeEnabled,
  enableDetachedMode,
  dropdownTitle,
  shareLink,
  apiToken,
  saveNoteAsTemplate,
  canSaveAsTemplate,
  onRowClick,
  lockNoteForEdition,
  unlockNoteForEdition,
  isPayingOrganization,
}) {
  const [
    editProtectionIncentiveModalIsOpen,
    setEditProtectionInventiveModalIsOpen,
  ] = React.useState(false)

  if (!note) {
    return null
  }

  const isPubliclyShared = Boolean(note.publiclySharedAt)
  const items = [
    {
      icon: InfoIcon,
      isAllowed: true,
      title: 'Information',
      action: () => {
        setActiveModal({
          id: NOTES_INFOS,
          meta: { noteId: note.id },
        })
      },
    },
    {
      icon: RecentIcon,
      title: 'History',
      isAllowed: !showListControls,
      action: () => {
        setActiveModal({
          id: NOTES_HISTORY,
          meta: { noteId: note.id },
        })
      },
    },
    {
      icon: ArrowRightIcon,
      title: 'Move to',
      isAllowed: !isArchived && canEdit,
      action: () => {
        setActiveModal({
          id: MOVE_TO,
          meta: { noteIds: [note.id] },
        })
      },
    },

    {
      icon: ExternalLinkIcon,
      title: 'Open in new window',
      isAllowed: !isDetachedModeEnabled && isAppDesktop(),
      action: () => {
        enableDetachedMode({ noteId: note.id })
      },
    },

    {
      icon: DuplicateIcon,
      title: 'Duplicate',
      isAllowed: !isArchived && canCreate,
      action: () => {
        duplicateNote({ noteId: note.id })
      },
    },

    {
      icon: FavoriteEmptyIcon,
      title: 'Favorite',
      isAllowed: !isBookmarked,
      action: () => {
        updateUserPreferences({
          bookmarkedNoteIds: bookmarkedNoteIds.add(note.id),
        })
      },
    },

    {
      icon: FavoriteIcon,
      title: 'Unfavorite',
      isAllowed: isBookmarked,
      action: () => {
        updateUserPreferences({
          bookmarkedNoteIds: bookmarkedNoteIds.filter(
            bookmarkedNoteId => bookmarkedNoteId !== note.id
          ),
        })
      },
    },

    {
      icon: NewTemplateIcon,
      title: 'Save to templates',
      isAllowed: canSaveAsTemplate,
      action: async () => {
        saveNoteAsTemplate({ sourceNoteId: note.id })
      },
    },

    {
      icon: FilePDFIcon,
      title: 'PDF export',
      isAllowed: !isArchived && !showListControls,
      href: `${shareLink}.pdf?download=true`,
      action: () => {
        window.open(`${shareLink}.pdf?download=true`, '_blank')
      },
    },

    {
      icon: FileMDIcon,
      title: 'Markdown export',
      isAllowed: !isArchived && !showListControls,
      href: `${shareLink}.md?apiToken=${apiToken}&download=true`,
      action: () => {
        window.open(
          `${shareLink}.md?apiToken=${apiToken}&download=true`,
          '_blank'
        )
      },
    },

    {
      icon: note.isLocked ? PenCrossedIcon : PenIcon,
      isAllowed: canManage && !isArchived && !showListControls,
      title: (
        <TooltipOnHover
          id="note-direct-edit-toggle"
          placement="top"
          tooltipContent="Upgrade to enable this feature"
          disabled={isPayingOrganization}
        >
          <div
            className={classNames(
              'flexbox flexbox-align-center flexbox-space-between',
              { 'cursor-forbidden': !isPayingOrganization }
            )}
          >
            <span>Protect editing</span>
            <Toggle
              // * Force re-render on prop update
              // * We don't use a controlled component because it might cause weird things
              // * With the desync from the API when the call fails
              key={note.isLocked}
              small
              className="cursor"
              onClick={event => {
                // Don't let this bubble to avoid double triggers of `action`
                event.stopPropagation()
                event.preventDefault()
              }}
              defaultChecked={note.isLocked}
            />
          </div>
        </TooltipOnHover>
      ),
      action: () => {
        if (!isPayingOrganization) {
          setEditProtectionInventiveModalIsOpen(true)
          return
        }

        if (note.isLocked) {
          return unlockNoteForEdition({ id: note.id })
        }

        return lockNoteForEdition({ id: note.id })
      },
      onRowClick: () => {
        // noop to avoid closing while using the toggle
      },
    },

    {
      icon: LinkIcon,
      title: isPubliclyShared ? 'Copy public link' : 'Copy private link',
      isAllowed: !isArchived && showListControls,
      action: () => {
        copy(shareLink)
        createSuccessToasterMessage({
          title: 'Link copied to clipboard!',
        })
      },
    },

    {
      icon: DeleteIcon,
      title: 'Send to Trash',
      isAllowed: !isArchived && canEdit,
      action: () => {
        archiveNotes({ noteIds: [note.id] })
      },
    },

    {
      icon: UndoIcon,
      title: 'Restore to channel',
      isAllowed: isArchived && canManage,
      action: () => {
        unarchiveNotes({ noteIds: [note.id] })
      },
    },

    {
      icon: DeleteIcon,
      title: 'Delete',
      isAllowed: isArchived && canDelete,
      onRowClick: () => {
        // don't trigger rowClick to avoid closing popover until Confirmation action
      },
      confirm: () => (
        <div>
          <div className="bold font-size-large center-text margin-bottom-large">
            Are you sure?
          </div>
          You are about to delete{' '}
          {note.title ? (
            <span className="bold">"{note.title}"</span>
          ) : (
            ' this doc'
          )}{' '}
          forever. This action is irrevocable!
        </div>
      ),
      action: () => {
        deleteNotes({ noteIds: [note.id] })
      },
    },
  ]

  return (
    <React.Fragment>
      {editProtectionIncentiveModalIsOpen && (
        <EditProtectionIncentiveModal
          onModalClose={() => setEditProtectionInventiveModalIsOpen(false)}
        />
      )}
      <Menu onRowClick={onRowClick} items={items} title={dropdownTitle} />
    </React.Fragment>
  )
})
